import { Box, Button, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import { AxiosResponse } from 'axios';
import Breadcrumbs from 'components/breadcrumbs';
import List from 'components/list/list';
import ListCell from 'components/list/list-cell';
import ListRow from 'components/list/list-row';

import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import { useAccountState } from 'contexts/account/account-context';
import { SnackbarActionTypes, useSnackbarDispatch } from 'contexts/snackbar/SnackbarContext';
import { useGet } from 'hooks/use-get';
import { usePost } from 'hooks/use-post';

import { useQueryClient } from '@tanstack/react-query';
import Paper from 'components/paper';
import React, { useEffect } from 'react';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import validateProfileOwner from 'utils/validate-profile-owner';
import { getProfilesAssortment } from 'views/planning-prognosis/api/get-profiles';
import { UpdateDefaultMarketProfileInput } from 'views/profiles-strategies/features/market-profiles/types/update-default-market-profile-input.type';
import ProfilesStrategiesTabHeader from '../../components/profile-strategies-tabs-header';
import { getDefaultMarketProfile } from './api/get-default-market-profile';
import { putDefaultMarketProfile } from './api/update-default-market-profile';

type Props = {};

const EditMarketProfiles: React.FC<Props> = () => {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const snackbarDispatch = useSnackbarDispatch();
  const { account } = useAccountState();

  const PAGE_NAME_ID = 'profilesStrategies.editMarketProfiles';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // #region FORMS

  const { handleSubmit, control, reset } = useForm<UpdateDefaultMarketProfileInput>({
    defaultValues: {
      smoltTyped: [],
    },
    shouldUnregister: false,
  });

  const { fields: fieldsProfiles } = useFieldArray({
    control: control,
    name: 'smoltTyped',
    shouldUnregister: false,
  });

  const watchSmoltTyped = useWatch({
    control,
    name: 'smoltTyped',
  });

  // #endregion FORMS

  // #region FETCH DATA

  const { data: defaultProfiles, isFetching } = useGet(
    ['getPlanAndPrognosisMarketDefaultProfile', account.company?.marketId],
    ({ queryKey }) => getDefaultMarketProfile(Number(queryKey[1])),
    {
      enabled: account.company?.features.hasPlanningPrognosisManageMarketProfiles && !!account.company?.marketId,
    }
  );

  useEffect(() => {
    if (!defaultProfiles) return;

    reset({
      smoltTyped: defaultProfiles.smoltTyped.map((st) => ({
        efiProfileId: st.efi?.profileId ?? 0,
        egiProfileId: st.egi?.profileId ?? 0,
        feedStrategyId: st.feedStrategy?.profileId ?? 0,
        mortalityProfileId: st.mortality?.profileId ?? 0,
        smoltTypeId: st.smoltTypeId ?? 0,
      })),
    });
  }, [defaultProfiles, reset]);

  const { data: profiles } = useGet(
    ['getProfilesAssortment', account.company?.marketId, account.company?.companyId],
    ({ queryKey }) => getProfilesAssortment(Number(queryKey[1]), String(queryKey[2])),
    {
      enabled: !!account.company?.marketId && !!account.company?.companyId,
    }
  );

  // #endregion FETCH DATA

  // #region POST DATA

  const updateDefaultProfile = usePost<AxiosResponse, UpdateDefaultMarketProfileInput>(
    (request) => putDefaultMarketProfile(Number(account.company?.marketId), request),
    () => {
      queryClient.invalidateQueries({
        queryKey: ['getPlanAndPrognosisMarketDefaultProfile', account.company?.marketId],
        exact: true,
      });
    },
    (error) => {
      snackbarDispatch({
        type: SnackbarActionTypes.ERROR_SNACKBAR,
        payload: { msg: intl.formatMessage({ id: 'snackbar.errors.default' }), error: error },
      });
    }
  );

  // #endregion POST DATA

  // #region HANDLERS

  const handleUpdateProfiles = (request: UpdateDefaultMarketProfileInput) => {
    if (!!defaultProfiles) {
      updateDefaultProfile.mutate({
        ...request,
        temperatureProfileId: defaultProfiles.temperatureProfile?.profileId ?? null,
        fallbackEfiProfileId: defaultProfiles.efiFallback?.profileId ?? null,
        fallbackEgiProfileId: defaultProfiles.egiFallback?.profileId ?? null,
        fallbackMortalityProfileId: defaultProfiles.mortalityFallback?.profileId ?? null,
      });
    }
  };

  // #endregion HANDLERS

  return (
    <>
      <ProfilesStrategiesTabHeader utils={<Breadcrumbs />} />
      <form onSubmit={handleSubmit(handleUpdateProfiles)}>
        <Paper transparent>
          <List
            options={[
              { id: 'speciesName', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.speciesNameLabel` }) },
              { id: 'smoltTypeName', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.smoltTypeNameLabel` }) },
              { id: 'egi', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.egiLabel` }) },
              { id: 'efi', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.efiLabel` }) },
              { id: 'mortality', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.mortalityLabel` }) },
              { id: 'feedStrategy', label: intl.formatMessage({ id: `${PAGE_NAME_ID}.list.feedStrategyLabel` }) },
            ]}
            isLoading={isFetching}
            noResult={{
              show: !!defaultProfiles && defaultProfiles.smoltTyped?.length <= 0,
              text: <FormattedMessage id={`${PAGE_NAME_ID}.list.noResults`} />,
            }}>
            {fieldsProfiles.map((row, idx) => (
              <ListRow key={row.id}>
                {isMobile ? (
                  <Box display={'flex'} flexDirection={'column'} gap={2}>
                    <ListCell align="left">
                      {defaultProfiles?.smoltTyped.find((s) => s.smoltTypeId === row.smoltTypeId)?.speciesName}
                    </ListCell>
                    <ListCell align="left">
                      {defaultProfiles?.smoltTyped.find((s) => s.smoltTypeId === row.smoltTypeId)?.smoltTypeName}
                    </ListCell>
                    <Select
                      dark
                      hiddenLabel
                      label="EGI"
                      size="small"
                      controller={{
                        control: control,
                        name: `smoltTyped.${idx}.egiProfileId`,
                        rules: {
                          required: true,
                          validate: {
                            invalidId: (v) => (v !== 0 ? true : false),
                          },
                        },
                      }}>
                      {profiles?.egi
                        ?.filter((profile) =>
                          validateProfileOwner(profile, account.company?.marketId, String(account.company?.companyId))
                        )
                        .map((row) => (
                          <RadioSelectItem key={row.id} value={row.id} selected={row.id === watchSmoltTyped[idx]?.egiProfileId}>
                            {row.name} {row.ownerType}
                          </RadioSelectItem>
                        ))}
                    </Select>
                    <Select
                      dark
                      hiddenLabel
                      margin="none"
                      label="EFI"
                      size="small"
                      controller={{
                        control: control,
                        name: `smoltTyped.${idx}.efiProfileId`,
                        rules: {
                          required: true,
                          validate: {
                            invalidId: (v) => (v !== 0 ? true : false),
                          },
                        },
                      }}>
                      {profiles?.efi
                        ?.filter((profile) =>
                          validateProfileOwner(profile, account.company?.marketId, String(account.company?.companyId))
                        )
                        .map((row) => (
                          <RadioSelectItem key={row.id} value={row.id} selected={row.id === watchSmoltTyped[idx]?.efiProfileId}>
                            {row.name}
                          </RadioSelectItem>
                        ))}
                    </Select>
                    <Select
                      dark
                      hiddenLabel
                      margin="none"
                      label="mortality"
                      size="small"
                      controller={{
                        control: control,
                        name: `smoltTyped.${idx}.mortalityProfileId`,
                        rules: {
                          required: true,
                          validate: {
                            invalidId: (v) => (v !== 0 ? true : false),
                          },
                        },
                      }}>
                      {profiles?.mort
                        ?.filter((profile) =>
                          validateProfileOwner(profile, account.company?.marketId, String(account.company?.companyId))
                        )
                        .map((row) => (
                          <RadioSelectItem key={row.id} value={row.id} selected={row.id === watchSmoltTyped[idx]?.mortalityProfileId}>
                            {row.name}
                          </RadioSelectItem>
                        ))}
                    </Select>
                    <Select
                      dark
                      hiddenLabel
                      margin="none"
                      label="Feed Strategy"
                      size="small"
                      controller={{
                        control: control,
                        name: `smoltTyped.${idx}.feedStrategyId`,
                        rules: {
                          required: true,
                          validate: {
                            invalidId: (v) => (v !== 0 ? true : false),
                          },
                        },
                      }}>
                      {profiles?.feed
                        .filter((profile) =>
                          validateProfileOwner(profile, account.company?.marketId, String(account.company?.companyId))
                        )
                        .map((row) => (
                          <RadioSelectItem key={row.id} value={row.id} selected={row.id === watchSmoltTyped[idx]?.feedStrategyId}>
                            {row.name}
                          </RadioSelectItem>
                        ))}
                    </Select>
                  </Box>
                ) : (
                  <>
                    <ListCell align="left">
                      {defaultProfiles?.smoltTyped.find((s) => s.smoltTypeId === row.smoltTypeId)?.speciesName}
                    </ListCell>
                    <ListCell align="left">
                      {defaultProfiles?.smoltTyped.find((s) => s.smoltTypeId === row.smoltTypeId)?.smoltTypeName}
                    </ListCell>
                    <ListCell align="left">
                      <Select
                        dark
                        hiddenLabel
                        label="EGI"
                        fullWidth
                        controller={{
                          control: control,
                          name: `smoltTyped.${idx}.egiProfileId`,
                          rules: {
                            required: true,
                            validate: {
                              invalidId: (v) => (v !== 0 ? true : false),
                            },
                          },
                        }}>
                        {profiles?.egi
                          ?.filter((profile) =>
                            validateProfileOwner(profile, account.company?.marketId, String(account.company?.companyId))
                          )
                          .map((row) => (
                            <RadioSelectItem key={row.id} value={row.id} selected={row.id === watchSmoltTyped[idx]?.egiProfileId}>
                              {row.name} {row.ownerType}
                            </RadioSelectItem>
                          ))}
                      </Select>
                    </ListCell>
                    <ListCell align="left">
                      <Select
                        dark
                        hiddenLabel
                        fullWidth
                        label="EFI"
                        controller={{
                          control: control,
                          name: `smoltTyped.${idx}.efiProfileId`,
                          rules: {
                            required: true,
                            validate: {
                              invalidId: (v) => (v !== 0 ? true : false),
                            },
                          },
                        }}>
                        {profiles?.efi
                          ?.filter((profile) =>
                            validateProfileOwner(profile, account.company?.marketId, String(account.company?.companyId))
                          )
                          .map((row) => (
                            <RadioSelectItem key={row.id} value={row.id} selected={row.id === watchSmoltTyped[idx]?.efiProfileId}>
                              {row.name}
                            </RadioSelectItem>
                          ))}
                      </Select>
                    </ListCell>
                    <ListCell align="left">
                      <Select
                        dark
                        hiddenLabel
                        fullWidth
                        label="mortality"
                        controller={{
                          control: control,
                          name: `smoltTyped.${idx}.mortalityProfileId`,
                          rules: {
                            required: true,
                            validate: {
                              invalidId: (v) => (v !== 0 ? true : false),
                            },
                          },
                        }}>
                        {profiles?.mort
                          ?.filter((profile) =>
                            validateProfileOwner(profile, account.company?.marketId, String(account.company?.companyId))
                          )
                          .map((row) => (
                            <RadioSelectItem
                              key={row.id}
                              value={row.id}
                              selected={row.id === watchSmoltTyped[idx]?.mortalityProfileId}>
                              {row.name}
                            </RadioSelectItem>
                          ))}
                      </Select>
                    </ListCell>
                    <ListCell align="left">
                      <Select
                        dark
                        hiddenLabel
                        fullWidth
                        label="Feed Strategy"
                        controller={{
                          control: control,
                          name: `smoltTyped.${idx}.feedStrategyId`,
                          rules: {
                            required: true,
                            validate: {
                              invalidId: (v) => (v !== 0 ? true : false),
                            },
                          },
                        }}>
                        {profiles?.feed
                          .filter((profile) =>
                            validateProfileOwner(profile, account.company?.marketId, String(account.company?.companyId))
                          )
                          .map((row) => (
                            <RadioSelectItem key={row.id} value={row.id} selected={row.id === watchSmoltTyped[idx]?.feedStrategyId}>
                              {row.name}
                            </RadioSelectItem>
                          ))}
                      </Select>
                    </ListCell>
                  </>
                )}
              </ListRow>
            ))}
          </List>
          <Box display="flex" justifyContent="flex-end" my={4}>
            <Button type="submit" disabled={updateDefaultProfile.isPending}>
              {updateDefaultProfile.isPending ? (
                <CircularProgress color="inherit" />
              ) : (
                <FormattedMessage id={`${PAGE_NAME_ID}.submitButton`} />
              )}
            </Button>
          </Box>
        </Paper>
      </form>
    </>
  );
};

export default EditMarketProfiles;
